import { useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'

import { Button, Modal, Space, Table } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { useApi } from 'admin/lib/Api'
import { RecordIcon } from 'admin/lib/Icons/RecordIcon'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'
import { addSpanToWhitespace } from 'admin/lib/Functions/whiteSpaceHelper'

export function GroupUpdateLogEntryDetails({
  logId,
  fieldName,
  formattedFieldName,
  moduleName,
  handleModalClose,
  accounts,
  projects,
  onRevert
}) {
  const [visible, setVisible] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const [showRevertModal, setShowRevertModal] = useState(false)
  const [processing, setProcessing] = useState(false)

  const api = useApi()

  useEffect(() => {
    const handleLoad = async logId => {
      return await api.getViewMassUpdateDiff(logId)
    }
    setVisible(true)
    setIsLoading(true)
    handleLoad(logId)
      .then(value => {
        setData(value[0])
        setIsLoading(false)
      })
      .catch(() => {
        setData([])
      })
  }, [api, logId])

  const formatTableData = () => {
    let formattedData = []
    if (data && data.diffs && data.diffs.length > 0) {
      formattedData = data.diffs.map(item => {
        let formattedOldValue
        if (fieldName === 'products.givenCertifications') {
          formattedOldValue = JSON.parse(item.oldValue)
          if (Array.isArray(formattedOldValue)) {
            formattedOldValue = formattedOldValue[0]
          }
        }
        return {
          key: JSON.stringify({
            oldValue: item.oldValue,
            updatedValue: item.updatedValue,
            currentValue: item.currentValue,
            index: item.index,
            recordId: item.recordId,
            projectId: item.projectId
          }),
          oldValue: formattedOldValue || item.oldValue,
          updatedValue: item.updatedValue,
          currentValue: item.currentValue,
          index: item.index,
          isModified: item.isModified,
          accountId: item.accountId,
          projectId: item.projectId,
          recordId: item.recordId,
          recordMissing: item.recordMissing
        }
      })
    }

    return formattedData
  }

  const columns = [
    {
      title: 'Old value',
      dataIndex: 'oldValue',
      render: value => addSpanToWhitespace(value)
    },
    {
      title: 'Updated value',
      dataIndex: 'updatedValue',
      render: value => addSpanToWhitespace(value)
    },
    {
      title: 'Current value',
      dataIndex: 'currentValue',
      render: (value, record) => {
        if (record.isModified) {
          return <b>{addSpanToWhitespace(value)}</b>
        }
        return addSpanToWhitespace(value)
      }
    },
    {
      title: 'Account',
      dataIndex: 'accountId',
      render: value => accounts[value].name
    },
    {
      title: 'Project',
      dataIndex: 'projectId',
      render: value => projects[value]
    },
    {
      title: 'Record',
      dataIndex: 'recordId',
      width: '80px',
      align: 'center',
      render: (value, record) => {
        if (record.recordMissing) {
          return 'Record missing'
        }

        return (
          <StyledEditLink
            href={generatePath(ROUTES.RECORD.EDIT, {
              accountId: record.accountId,
              projectId: record.projectId,
              moduleName: moduleName,
              recordId: value
            })}
            onClick={e => {
              e.preventDefault()
              window.open(
                generatePath(ROUTES.RECORD.EDIT, {
                  accountId: record.accountId,
                  projectId: record.projectId,
                  moduleName: moduleName,
                  recordId: value
                }),
                '_blank',
                'noreferrer'
              )
            }}
          >
            <RecordIcon />
          </StyledEditLink>
        )
      }
    }
  ]

  // If field name starts with 'item.' or 'products.' show index in 4th column
  // Added 1 to value because array is 0-indexed
  if (fieldName.startsWith('items.')) {
    columns.splice(3, 0, {
      title: 'Item number',
      dataIndex: 'index',
      align: 'center',
      render: value => value + 1
    })
  } else if (fieldName.startsWith('products.')) {
    columns.splice(3, 0, {
      title: 'Product number',
      dataIndex: 'index',
      align: 'center',
      render: value => value + 1
    })
  }

  const onClose = () => {
    setVisible(false)
    handleModalClose()
  }

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    columnWidth: '48px',
    onChange: onSelectChange,
    getCheckboxProps: record => {
      if (record.oldValue === record.currentValue)
        return {
          disabled: true
        }
    }
  }

  const showRevertRecords = () => {
    setShowRevertModal(true)
  }

  const handleRevertModalClose = () => {
    setShowRevertModal(false)
  }

  const handleRevertRecords = async () => {
    setProcessing(true)
    const entries = selectedRowKeys.map(item => JSON.parse(item))
    const isChildField =
      fieldName.startsWith('items.') || fieldName.startsWith('products.')

    const numOfRevertableRows = data.diffs.filter(
      diff => diff.oldValue !== diff.currentValue
    ).length
    const reversalStatus =
      selectedRowKeys.length === numOfRevertableRows
        ? 'Reverted'
        : 'PartialReversal'

    // await API call
    await api.revertRecordsFieldValues(
      logId,
      entries,
      fieldName,
      isChildField,
      moduleName,
      reversalStatus
    )

    setProcessing(false)
    handleRevertModalClose()
    onClose()
    onRevert()
  }

  return (
    <>
      <Modal
        title="Details"
        open={visible}
        closable
        onCancel={onClose}
        maskClosable
        footer={[
          <Button key="back" onClick={onClose} type="secondary">
            Close
          </Button>
        ]}
        styles={{ body: { height: '70vh' } }}
        width={'80vw'}
      >
        {isLoading && (
          <div style={{ margin: '0 auto', width: '200px' }}>
            <ScreenSpin id="userLoadingSpinner" />
          </div>
        )}
        {!isLoading && (
          <>
            <Space
              direction="horizontal"
              id="action-bar"
              style={{ marginBottom: '1.5rem' }}
            >
              <div className="total-records" id="totalRecordsCount">
                Selected:{' '}
                <span style={{ fontWeight: '700' }}>
                  {selectedRowKeys.length} record
                  {selectedRowKeys.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="total-records">
                Field:{' '}
                <span style={{ fontWeight: '700' }}>{formattedFieldName}</span>
              </div>
              <Button
                type="primary"
                style={{ marginLeft: '0.5rem' }}
                onClick={showRevertRecords}
                disabled={selectedRowKeys.length < 1}
              >
                Revert records
              </Button>
            </Space>
            <Table
              columns={columns}
              dataSource={formatTableData()}
              onRow={record => ({
                style: {
                  backgroundColor: record.isModified
                    ? 'rgba(255,0,0,0.075)'
                    : 'default'
                }
              })}
              pagination={false}
              rowSelection={rowSelection}
              scroll={{ y: 'calc(70vh - 160px)' }}
              sticky
            />
          </>
        )}
      </Modal>

      <Modal
        open={showRevertModal}
        title={`Revert record${selectedRowKeys.length === 1 ? '' : 's'}`}
        styles={{ body: { minHeight: '168px' } }}
        centered
        onOk={handleRevertRecords}
        onCancel={handleRevertModalClose}
        okButtonProps={{
          loading: processing,
          style: {
            minWidth: '130px'
          },
          type: 'danger'
        }}
        okText={
          processing
            ? `Reverting record${selectedRowKeys.length === 1 ? '' : 's'}`
            : `Revert record${selectedRowKeys.length === 1 ? '' : 's'}`
        }
        cancelButtonProps={{ disabled: processing, type: 'secondary' }}
      >
        <div>
          <p className="large-text">
            You're about to undo the changes made to:
            <br />
            <b>{formattedFieldName}</b> in{' '}
            <b>
              {selectedRowKeys.length} record
              {selectedRowKeys.length === 1 ? '' : 's'}
            </b>
            .
          </p>
        </div>
      </Modal>
    </>
  )
}

const StyledEditLink = styled.a`
  display: inline-block;
  border-bottom: transparent 1px solid;

  :visited {
    color: ${colours.DEEP_SKY_BLUE};
  }

  :focus {
    border-bottom: 2px solid;
  }

  :hover {
    border-bottom: 1px solid;
    background: ${colours.PALE_GREY};
  }
`
